<template>
  <m-input
    v-model="textValue"
    class="date-input"
    :required="$attrs.required"
    :disabled="$attrs.disabled || $attrs.disable"
    :disable="$attrs.disabled || $attrs.disable"
    :data-testid="$attrs['data-testid']"
    :data-focus-first="$attrs['data-focus-first']"
    :label="label"
    :sr-label="srLabel"
    :placeholder="placeholder"
    :rules="rules"
    :required-text="requiredText || `Enter a date for '${srLabel || label}'`"
    :horizontal="horizontal"
    :label-width="labelWidth"
    :show-all-errors="showAllErrors"
    :input-format="dateMask"
    :large="large"
    :name="name"
    :helper="helper"
    :instructions="instructions"
    :actions="actions"
    :actions-left="actionsLeft"
    @blur="onBlur"
    @focus="onFocus"
    @keydown.capture="onKeydown"
  >
    <template v-if="$slots.helper" #helper> <slot name="helper" /></template>

    <template #append>
      <m-button
        ref="calendarButton"
        sr-label="Open date picker"
        icon="fa-light fa-calendar"
        icon-only
        ghost
        small
        color="secondary"
        aria-haspopup="dialog"
        :aria-expanded="showForm"
        :aria-controls="id"
        @click="showForm = !showForm"
      >
        <span class="sr-only">Open date picker for {{ srLabel || splitByUpperCase(name) }}</span>
      </m-button>
    </template>
  </m-input>

  <Teleport to="body">
    <div v-if="showForm" :id="id" ref="popupEl" :style="popupStyle">
      <m-calendar-grid
        :model-value="input"
        :min-date="resolvedMin"
        :max-date="resolvedMax"
        @update:model-value="onCalendarSelect"
        @close="showForm = false"
      >
        <template #header-actions>
          <m-button
            icon="fa-solid fa-xmark"
            icon-only
            color="secondary"
            sr-label="Close"
            :outline="false"
            :small="true"
            :dense="true"
            class="borderless"
            @click="showForm = false"
          />
        </template>
      </m-calendar-grid>
    </div>
  </Teleport>
</template>

<script lang="ts" setup>
import MButton from "../../MButton";
import MInput from "./../MInput";
import MCalendarGrid from "../MCalendarGrid/MCalendarGrid.vue";

import { computed, nextTick, onBeforeMount, provide, ref, watch } from "vue";
import { useDateValue } from "../../../../composables/dateValue";
import { usePopupPosition } from "../../../../composables/usePopupPosition";
import { addToDate, formatDateTimeMedicus } from "../../../../utils/date";
import { getMaxDate, getMinDate, randomID } from "../../../../utils/helpers";
import { splitByUpperCase } from "../../../../utils/string";

defineOptions({ inheritAttrs: false });

const props = defineProps({
  modelValue: {
    type: [Object, String, Number, Array, Date],
    required: false,
  },
  name: String,
  label: String,
  srLabel: String,
  rules: Array as () => Array<(v: any) => boolean | string>,
  showAllErrors: Boolean,
  // m-labelled text props
  horizontal: {
    type: Boolean,
    default: undefined,
  },
  labelWidth: String,

  placeholder: {
    type: String,
    default: "dd/mm/yyyy",
  },

  minDate: {
    type: [Date, Number, String],
    default() {
      return new Date(1000, 1, 1);
    },
  },
  maxDate: {
    type: [Date, Number, String],
    default() {
      return new Date(new Date().getFullYear() + 100, 1, 1);
    },
  },

  requiredText: String,

  minDateText: {
    type: String,
  },
  maxDateText: {
    type: String,
  },

  large: Boolean,

  helper: String,
  instructions: String,

  masked: Boolean,

  actions: Array,
  actionsLeft: Boolean,
});

onBeforeMount(() => {
  if (!props.label && !props.srLabel) {
    // TODO, this is temporary as checkLabel does not work anymore
    console.warn("Either label or srLabel must be provided");

    // eslint-disable-next-line no-undef
    if (IS_APP) {
      // more info needed to actually find it on the page. DEV only.
      console.warn(props);
    }
    // Optionally throw error:
    // throw new Error('Either "label" or "srLabel" prop is required.');
  }
});

provide("input-dont-use-name", true);

const showForm = ref(false);
const id = randomID("data-picker");

const calendarButton = ref<typeof MButton>(null);

const { popupEl, popupStyle } = usePopupPosition({
  isOpen: showForm,
  getTrigger: () => calendarButton.value?.$el as HTMLElement | null,
});

const { dateText, rules, input, displayDate } = useDateValue(
  props,
  false,
  undefined,
  props.rules,
  () => props.requiredText || `Enter a date for '${props.label}'`,
);

const textValue = ref(displayDate.value);
const shouldMask = ref(false);

const dateMask = computed(() => (shouldMask.value ? "dd/mm/yyyy" : undefined));

const resolvedMin = computed(() => getMinDate(props.minDate, false));
const resolvedMax = computed(() => getMaxDate(props.maxDate, false));

function onCalendarSelect(value: string) {
  input.value = value;
  showForm.value = false;
}

function parseDate(d: string, splitter: string) {
  let [day, month, year, ...rest] = d.split(splitter);

  if (!day || !month || !year || rest?.length) {
    return undefined;
  }

  if (year && +year < 100) {
    year = "20" + year.padStart(2, "0");
  }

  if (month && +month < 10) {
    month = month.padStart(2, "0");
  }

  if (day && +day < 10) {
    day = day.padStart(2, "0");
  }

  return [day, month, year].join("/");
}

const tokenRegex = /-?(\d+)([d|w|m|y])/g;

function parseToken(t: string) {
  const matches = t.match(tokenRegex);
  if (!matches) {
    return;
  }

  let d = new Date();

  for (const t of matches) {
    const v = +t.slice(0, -1);

    switch (t[t.length - 1]) {
      case "d": {
        d = addToDate(d, { days: v });
        break;
      }
      case "w": {
        d = addToDate(d, { days: v * 7 });
        break;
      }
      case "m": {
        d = addToDate(d, { months: v });
        break;
      }
      case "y": {
        d = addToDate(d, { years: v });
        break;
      }
    }
  }
  return formatDateTimeMedicus(d);
}

function onKeydown(e: KeyboardEvent) {
  if (props.masked) return;

  switch (e.key?.toLowerCase()) {
    case "-":
    case "d":
    case "w":
    case "m":
    case "y":
      shouldMask.value = false;
      e.stopImmediatePropagation();
      break;
    case "/":
      shouldMask.value = true;
      break;
    default: {
      if (e.code.startsWith("Key") && !e.altKey && !e.shiftKey && !e.ctrlKey) {
        e.preventDefault();
      }
    }
  }
}

function onFocus() {
  textValue.value = dateText.value;
  nextTick(() => {
    // shouldMask.value = true;
    shouldMask.value = !!textValue.value;
  });
}

async function onBlur(e?: Event) {
  try {
    if (e && (e.target as HTMLElement).classList.contains("m-input--input-box")) {
      e.preventFormTouch = true;
      return;
    }
    shouldMask.value = false;
    if (props.masked) {
      dateText.value = textValue.value;
      return;
    }

    let text = textValue.value;
    if (!text) {
      // if the text is empty
      input.value = "";
      return;
    }

    // if the text is a number and has 6 or 8 digits (14022024 or 140224)
    if (text.match(/^(\d{6}|\d{8})$/)) {
      text = `${text.slice(0, 2)}/${text.slice(2, 4)}/${text.slice(4)}`;
    }

    let actualDate = parseDate(text, "/");
    if (!actualDate) {
      actualDate = parseDate(text, ".");
      if (!actualDate) {
        actualDate = parseToken(text.toLowerCase());
        if (!actualDate) {
          // invalid date
          return;
        }
      }
    }
    input.value = actualDate.split(" ")[0].split("-").reverse().join("/");
  } finally {
    shouldMask.value = false;
    await nextTick();
    textValue.value = displayDate.value;
  }
}

watch(showForm, (open) => {
  if (!open) nextTick(() => (calendarButton.value as any)?.focus());
});

watch(displayDate, (t, p) => {
  if (textValue.value === p) {
    textValue.value = t;
  }
});

watch(textValue, (t) => {
  // slash cannot be the first character
  shouldMask.value = t?.indexOf("/") > 0;
});
</script>
<style lang="scss">
.date-input {
  .m-input--input-box {
    max-width: 11em;
  }
}
</style>
